import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Pencil, AlertTriangle, Loader2, TrendingUp, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AISummary } from "../components/AISummary";

const CATEGORY_COLORS = {
  Groceries: { bg: "bg-emerald-500", light: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-600 dark:text-emerald-400" },
  Dining: { bg: "bg-orange-500", light: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-600 dark:text-orange-400" },
  Transport: { bg: "bg-blue-500", light: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-600 dark:text-blue-400" },
  Travel: { bg: "bg-cyan-500", light: "bg-cyan-100 dark:bg-cyan-900/30", text: "text-cyan-600 dark:text-cyan-400" },
  Utilities: { bg: "bg-amber-500", light: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-600 dark:text-amber-400" },
  Entertainment: { bg: "bg-purple-500", light: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-600 dark:text-purple-400" },
  Shopping: { bg: "bg-pink-500", light: "bg-pink-100 dark:bg-pink-900/30", text: "text-pink-600 dark:text-pink-400" },
  Health: { bg: "bg-red-500", light: "bg-red-100 dark:bg-red-900/30", text: "text-red-600 dark:text-red-400" },
  Personal: { bg: "bg-rose-500", light: "bg-rose-100 dark:bg-rose-900/30", text: "text-rose-600 dark:text-rose-400" },
  Income: { bg: "bg-green-500", light: "bg-green-100 dark:bg-green-900/30", text: "text-green-600 dark:text-green-400" },
  Transfer: { bg: "bg-gray-500", light: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400" },
  Subscriptions: { bg: "bg-indigo-500", light: "bg-indigo-100 dark:bg-indigo-900/30", text: "text-indigo-600 dark:text-indigo-400" },
  Housing: { bg: "bg-teal-500", light: "bg-teal-100 dark:bg-teal-900/30", text: "text-teal-600 dark:text-teal-400" },
  "Debt Payment": { bg: "bg-red-600", light: "bg-red-100 dark:bg-red-900/30", text: "text-red-600 dark:text-red-400" },
  "Bank Fees": { bg: "bg-stone-500", light: "bg-stone-100 dark:bg-stone-900/30", text: "text-stone-600 dark:text-stone-400" },
  Other: { bg: "bg-slate-500", light: "bg-slate-100 dark:bg-slate-800", text: "text-slate-600 dark:text-slate-400" },
};

export const BudgetPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [budget, setBudget] = useState(null);
  const [rollovers, setRollovers] = useState([]);
  const [totalRollover, setTotalRollover] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLimits, setEditLimits] = useState({});
  const [saving, setSaving] = useState(false);
  const [showRollovers, setShowRollovers] = useState(false);

  const currentMonth = new Date().toISOString().slice(0, 7);

  const fetchBudget = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/budgets/${currentMonth}/with-rollovers`);
      setBudget(response.data);
      setRollovers(response.data.rollovers || []);
      setTotalRollover(response.data.total_rollover || 0);
    } catch (error) {
      // Fallback to regular budget endpoint if with-rollovers fails
      try {
        const response = await api.get(`/budgets/${currentMonth}`);
        setBudget(response.data);
      } catch (e) {
        console.error("Failed to fetch budget:", e);
        toast.error("Failed to load budget");
      }
    } finally {
      setLoading(false);
    }
  }, [api, currentMonth]);

  useEffect(() => {
    fetchBudget();
  }, [fetchBudget]);

  const handleOpenEdit = () => {
    const limits = {};
    if (budget && budget.categories) {
      budget.categories.forEach(cat => {
        limits[cat.category] = cat.limit_amount;
      });
    }
    setEditLimits(limits);
    setEditModalOpen(true);
  };

  const handleSaveBudget = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/budgets/${currentMonth}`, {
        categories: editLimits
      });
      setBudget(response.data);
      toast.success("Budget updated");
      setEditModalOpen(false);
    } catch (error) {
      toast.error("Failed to update budget");
    } finally {
      setSaving(false);
    }
  };

  const handleLimitChange = (category, value) => {
    setEditLimits(prev => ({
      ...prev,
      [category]: parseFloat(value) || 0
    }));
  };

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="budget-loading">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-32 mb-2" />
            <Skeleton className="h-5 w-48" />
          </div>
          <Skeleton className="h-10 w-32 rounded-full" />
        </div>
        <Skeleton className="h-32 rounded-xl" />
        <div className="grid gap-3">
          {[1,2,3,4,5].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const categories = budget?.categories || [];
  const overBudgetCount = categories.filter(c => c.percentage >= 100).length;
  const onTrackCount = categories.filter(c => c.percentage < 80).length;
  const totalPercentage = budget?.total_budget > 0 
    ? Math.round((budget?.total_spent / budget?.total_budget) * 100) 
    : 0;

  return (
    <div data-testid="budget-page">
      {/* Header */}
      <div className="phead">
        <div><h1>Budget</h1><div className="sub">{new Date().toLocaleDateString("en-CA", { month: "long", year: "numeric" })}</div></div>
        <div className="actions"><button className="btn btn-g" onClick={handleOpenEdit} data-testid="edit-budget-btn">
          <Pencil className="h-4 w-4" /> Edit budget
        </button></div>
      </div>

      {/* AI Summary */}
      <AISummary page="budget" />

      {/* Hero Card */}
      <div className="card hero" style={{padding:"28px 32px 30px",marginBottom:22}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:16}}>
          <div>
            <span className="lab">Total spent</span>
            <div style={{margin:"10px 0 4px"}}>
              <span className="serif" style={{fontSize:46,fontWeight:600,letterSpacing:"-1.5px",color:"#14432B"}}>{formatCurrency(budget?.total_spent || 0)}</span>
              <span className="muted" style={{fontSize:22}}> / {formatCurrency(budget?.total_budget || 0)}</span>
            </div>
            <div className="muted" style={{fontSize:14}}>{formatCurrency(Math.max(0,(budget?.total_budget||0)-(budget?.total_spent||0)))} left · {new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).getDate() - new Date().getDate()} days to go</div>
          </div>
          <div style={{textAlign:"right"}}>
            <span className={totalPercentage < 80 ? "pill fernp" : totalPercentage < 100 ? "pill" : "pill clayp"} style={{fontSize:13,padding:"7px 14px",borderRadius:50,background: totalPercentage < 80 ? "rgba(63,125,82,.1)" : totalPercentage < 100 ? "rgba(198,138,60,.1)" : "rgba(176,96,63,.1)",color: totalPercentage < 80 ? "#3F7D52" : totalPercentage < 100 ? "#C68A3C" : "#B0603F",fontWeight:600}}>
              {totalPercentage < 80 ? <><CheckCircle2 size={14} style={{marginRight:4}} /> On track</> : totalPercentage < 100 ? "Close" : <><AlertTriangle size={14} style={{marginRight:4}} /> Over budget</>}
            </span>
          </div>
        </div>
        <div className="bar" style={{marginTop:20,height:10}}><i style={{width:Math.min(totalPercentage,100)+"%",background: totalPercentage < 80 ? "#7ED957" : totalPercentage < 100 ? "#C68A3C" : "#B0603F"}} /></div>
      </div>

      {/* By Category */}
      <div className="sec-h" style={{margin:"4px 2px 14px"}}><h3 style={{fontSize:20}}>By category</h3><span className="muted" style={{fontSize:14}}>{categories.length} categories</span></div>
      <div className="card">
        {categories.map((cat) => {
          var pct = Math.min(cat.percentage || 0, 100);
          var barColor = pct >= 100 ? "#B0603F" : pct >= 80 ? "#C68A3C" : "#3F7D52";
          var remaining = Math.max(0, cat.budget - cat.spent);
          return (
            <div key={cat.category} className="bud" style={{padding:"16px 22px",borderBottom:"1px solid #ECE8DC"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,fontSize:14}}><b>{cat.category}</b><span>{formatCurrency(cat.spent)} <span className="muted">/ {formatCurrency(cat.budget)}</span></span></div>
              <div className="bar" style={{height:8}}><i style={{width:pct+"%",background:barColor}} /></div>
              <div className="left muted" style={{fontSize:13,marginTop:6}}>${remaining.toFixed(0)} left to spend</div>
            </div>
          );
        })}
      </div>

      {/* Rollover Card */}
      {rollovers.length > 0 && (
        <Card className={`border-border/50 ${totalRollover > 0 ? "border-emerald-500/30 bg-emerald-50/30 dark:bg-emerald-950/10" : totalRollover < 0 ? "border-amber-500/30 bg-amber-50/30 dark:bg-amber-950/10" : ""}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  totalRollover > 0 
                    ? "bg-emerald-100 dark:bg-emerald-900/30" 
                    : totalRollover < 0 
                      ? "bg-amber-100 dark:bg-amber-900/30"
                      : "bg-muted"
                }`}>
                  <Sparkles className={`w-5 h-5 ${
                    totalRollover > 0 
                      ? "text-emerald-600 dark:text-emerald-400" 
                      : totalRollover < 0 
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground"
                  }`} />
                </div>
                <div>
                  <p className="font-semibold">Budget Rollover</p>
                  <p className="text-sm text-muted-foreground">
                    {totalRollover > 0 
                      ? `${formatCurrency(totalRollover)} saved from last month` 
                      : totalRollover < 0 
                        ? `${formatCurrency(Math.abs(totalRollover))} overspent last month`
                        : "No rollover from last month"}
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowRollovers(!showRollovers)}
                className="text-primary"
                data-testid="toggle-rollovers-btn"
              >
                {showRollovers ? "Hide" : "Details"}
                <ArrowRight className={`w-4 h-4 ml-1 transition-transform ${showRollovers ? "rotate-90" : ""}`} />
              </Button>
            </div>
            
            {showRollovers && (
              <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
                {rollovers.map((r) => (
                  <div key={r.category} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{r.category}</span>
                    <span className={`font-medium tabular-nums ${
                      r.rollover_amount > 0 
                        ? "text-emerald-600 dark:text-emerald-400" 
                        : "text-amber-600 dark:text-amber-400"
                    }`}>
                      {r.rollover_amount > 0 ? "+" : ""}{formatCurrency(r.rollover_amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category budgets */}
      <div className="grid gap-3">
        {categories.map((category) => {
          const colors = CATEGORY_COLORS[category.category] || CATEGORY_COLORS.Other;
          const isOverBudget = category.percentage >= 100;
          const isWarning = category.percentage >= 80 && category.percentage < 100;
          
          return (
            <Card 
              key={category.category} 
              className={`border-border/50 transition-all ${
                isOverBudget ? "bg-red-50/50 dark:bg-red-950/10 border-red-200/50 dark:border-red-800/50" : ""
              }`}
              data-testid={`budget-category-${category.category.toLowerCase()}`}
            >
              <CardContent className="p-4 md:p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${colors.light} flex items-center justify-center`}>
                      <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{category.category}</h3>
                      <p className="text-sm text-muted-foreground tabular-nums">
                        {formatCurrency(category.spent_amount)} of {formatCurrency(category.limit_amount)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`font-bold text-lg tabular-nums ${
                      isOverBudget 
                        ? "text-red-600 dark:text-red-400" 
                        : isWarning
                          ? "text-amber-600 dark:text-amber-400"
                          : colors.text
                    }`}>
                      {Math.round(category.percentage)}%
                    </span>
                    {isOverBudget && (
                      <p className="text-xs text-red-600 dark:text-red-400 tabular-nums">
                        +{formatCurrency(category.spent_amount - category.limit_amount)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`absolute inset-y-0 left-0 rounded-full transition-all ${
                      isOverBudget ? "bg-red-500" : isWarning ? "bg-amber-500" : colors.bg
                    }`}
                    style={{ width: `${Math.min(category.percentage, 100)}%` }}
                  />
                </div>
                {category.remaining > 0 && !isOverBudget && (
                  <p className="text-xs text-muted-foreground mt-2 tabular-nums">
                    {formatCurrency(category.remaining)} left to spend
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit budget modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Budget</DialogTitle>
            <DialogDescription>
              Set monthly spending limits for each category
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] pr-4 -mr-4">
            <div className="space-y-4 py-2 pr-4">
              {Object.entries(editLimits).map(([category, limit]) => {
                const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.Other;
                return (
                  <div key={category} className="flex items-center gap-4">
                    <div className="flex items-center gap-3 min-w-[140px]">
                      <div className={`w-8 h-8 rounded-lg ${colors.light} flex items-center justify-center flex-shrink-0`}>
                        <div className={`w-2.5 h-2.5 rounded-full ${colors.bg}`} />
                      </div>
                      <Label className="text-sm font-medium">{category}</Label>
                    </div>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        type="number"
                        value={limit}
                        onChange={(e) => handleLimitChange(category, e.target.value)}
                        className="pl-7 h-10"
                        min={0}
                        step={50}
                        data-testid={`budget-input-${category.toLowerCase()}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button className="flex-1 rounded-full" onClick={handleSaveBudget} disabled={saving} data-testid="save-budget-btn">
              {saving ? (
                <span className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                "Save Budget"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
